import React, { useEffect, useRef } from 'react';
import { ReactPlayerProps } from './types';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

function getYouTubeId(url?: string): string | null {
  if (!url) return null;
  const match = url.match(
    /(?:youtube\.com\/.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/,
  );
  return match ? match[1] : null;
}

function getPlaylistId(url?: string): string | null {
  if (!url) return null;
  const match = url.match(/[?&]list=([^&]+)/);
  return match ? match[1] : null;
}

const ReactPlayer: React.FC<ReactPlayerProps> = ({
  url,
  playing,
  loop,
  muted,
  controls = true,
  width = '100%',
  height = '240px',
  volume = 0.8,
  seekToSeconds,
  onReady,
  onPlay,
  onPause,
  onBuffer,
  onBufferEnd,
  onEnded,
  onError,
  onDuration,
  onSeek,
  onProgress,
  config,
}) => {
  const playerRef = useRef<any>(null);
  const progressIntervalRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isUnmountingRef = useRef(false);
  // useRef로 고정된 id
  const playerDivId = useRef(
    `yt-player-${Math.random().toString(36).substr(2, 9)}`,
  ).current;

  // YouTube API 로드
  useEffect(() => {
    if (window.YT && window.YT.Player) return;
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.body.appendChild(tag);
  }, []);

  // url이 바뀔 때만 YT.Player 인스턴스 생성/제거
  useEffect(() => {
    // URL이 바뀌면 이 effect 실행이 유효한지 추적하는 로컬 플래그
    // isUnmountingRef는 컴포넌트 완전 언마운트 시에만 사용
    let cancelled = false;

    // URL 변경 시 isUnmountingRef를 반드시 리셋 (이전 cleanup이 true로 설정했을 수 있음)
    isUnmountingRef.current = false;

    // 기존 progress interval 즉시 정리
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    const waitForContainer = () => {
      if (cancelled || isUnmountingRef.current) return;
      const container = containerRef.current;

      if (container) {
        waitForYT();
      } else {
        setTimeout(waitForContainer, 100);
      }
    };

    const waitForYT = () => {
      if (cancelled || isUnmountingRef.current) return;

      if (window.YT && window.YT.Player) {
        const container = containerRef.current;
        if (!url || !container) return;

        // 기존 플레이어 정리
        if (playerRef.current) {
          try {
            if (playerRef.current.destroy) {
              playerRef.current.destroy();
            }
          } catch (e) {
            console.error('기존 플레이어 destroy 중 에러:', e);
          }
          playerRef.current = null;
        }

        if (cancelled || isUnmountingRef.current) return;

        const videoId = getYouTubeId(url);
        const playlistId = getPlaylistId(url);

        const playerConfig: any = {
          width,
          height,
          // youtube-nocookie.com: 유저 로그인 세션 쿠키를 전달하지 않음
          // → 다중 플레이어 동시 사용 시 "계정이 다른 곳에서 사용 중" 에러 방지
          host: 'https://www.youtube-nocookie.com',
          playerVars: {
            controls: controls ? 1 : 0,
            loop: loop ? 1 : 0,
            ...config?.youtube?.playerVars,
          },
          events: {
            onReady: (event: any) => {
              if (cancelled || isUnmountingRef.current) return;
              event.target.setVolume(volume * 100);
              if (muted) event.target.mute();
              else event.target.unMute();
              if (playing) event.target.playVideo();
              else event.target.pauseVideo();
              onReady && onReady();
              onDuration && onDuration(event.target.getDuration());
            },
            onStateChange: (event: any) => {
              if (cancelled || isUnmountingRef.current) return;
              switch (event.data) {
                case window.YT.PlayerState.PLAYING:
                  onPlay && onPlay();
                  break;
                case window.YT.PlayerState.PAUSED:
                  onPause && onPause();
                  break;
                case window.YT.PlayerState.ENDED:
                  onEnded && onEnded();
                  if (loop) event.target.playVideo();
                  break;
                case window.YT.PlayerState.BUFFERING:
                  onBuffer && onBuffer();
                  break;
                default:
                  break;
              }
            },
            onError: (event: any) => {
              console.error('YT.Player Error:', event);
              if (cancelled || isUnmountingRef.current) return;
              onError && onError(event);
            },
          },
        };

        if (videoId) {
          playerConfig.videoId = videoId;
        } else if (playlistId) {
          playerConfig.playerVars.listType = 'playlist';
          playerConfig.playerVars.list = playlistId;
        }

        try {
          container.innerHTML = '';
        } catch (e) {
          console.error('container 비우기 중 에러:', e);
        }

        const playerDiv = document.createElement('div');
        playerDiv.id = playerDivId;
        container.appendChild(playerDiv);

        playerRef.current = new window.YT.Player(playerDiv, playerConfig);

        if (onProgress) {
          progressIntervalRef.current = setInterval(() => {
            if (cancelled || isUnmountingRef.current) return;
            if (playerRef.current && playerRef.current.getCurrentTime) {
              const playedSeconds = playerRef.current.getCurrentTime();
              const duration = playerRef.current.getDuration();
              onProgress({
                played: playedSeconds / duration,
                playedSeconds,
                loaded: 1,
                loadedSeconds: duration,
              });
            }
          }, 1000);
        }
      } else {
        setTimeout(waitForYT, 100);
      }
    };

    waitForContainer();

    return () => {
      cancelled = true;
      isUnmountingRef.current = true;

      if (playerRef.current) {
        try {
          if (playerRef.current.destroy) {
            playerRef.current.destroy();
          }
          playerRef.current = null;
        } catch (e) {
          console.error('플레이어 cleanup 중 에러:', e);
        }
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      try {
        const container = containerRef.current;
        if (container && container.innerHTML) {
          container.innerHTML = '';
        }
      } catch (e) {
        console.error('container cleanup 중 에러:', e);
      }
    };
  }, [url, playerDivId, width, height]);

  // 컴포넌트 언마운트 시 cleanup
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
    };
  }, []);

  // seekToSeconds prop 변경 시 seek 실행
  useEffect(() => {
    if (
      seekToSeconds !== undefined &&
      seekToSeconds !== null &&
      playerRef.current &&
      typeof playerRef.current.seekTo === 'function' &&
      !isUnmountingRef.current
    ) {
      playerRef.current.seekTo(seekToSeconds, true);
      onSeek && onSeek(seekToSeconds);
    }
  }, [seekToSeconds]);

  // playing, muted, volume 등 prop 변경시 동기화
  useEffect(() => {
    if (
      playerRef.current &&
      typeof playerRef.current.playVideo === 'function' &&
      !isUnmountingRef.current
    ) {
      if (playing) playerRef.current.playVideo();
      else playerRef.current.pauseVideo();
      if (muted) playerRef.current.mute();
      else playerRef.current.unMute();
      playerRef.current.setVolume(volume * 100);
    }
  }, [playing, muted, volume]);

  return (
    <div
      ref={containerRef}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
        background: '#000',
      }}
    />
  );
};

export default ReactPlayer;
